import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Paginated } from '@timesheet/shared';
import { apiOrThrow } from '@/lib/api';

interface UseCrudOptions {
  /** API base path, e.g. '/admin/clients'. No trailing slash. */
  basePath: string;
  /** TanStack Query key root, e.g. ['clients']. */
  queryKey: readonly unknown[];
  /** Extra query params merged into list URL, e.g. `{ fiscalYear: 2025 }`. */
  listParams?: Record<string, string | number | boolean | undefined>;
}

/**
 * Shared CRUD wiring for any admin master-data resource.
 *
 *   const crud = useCrud<Client, ClientInput>({
 *     basePath: '/admin/clients',
 *     queryKey: ['clients'],
 *   });
 *   crud.list.data?.items
 *   crud.create.mutate(input)
 */
export function useCrud<TRow, TCreate, TUpdate = Partial<TCreate>>({
  basePath,
  queryKey,
  listParams,
}: UseCrudOptions) {
  const qc = useQueryClient();

  const params = new URLSearchParams();
  if (listParams) {
    for (const [k, v] of Object.entries(listParams)) {
      if (v !== undefined && v !== '') params.set(k, String(v));
    }
  }
  const search = params.toString();
  const listUrl = search ? `${basePath}?${search}` : basePath;

  const list = useQuery({
    queryKey: [...queryKey, 'list', listParams ?? {}],
    queryFn: () => apiOrThrow<Paginated<TRow>>(listUrl),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey });

  const create = useMutation({
    mutationFn: (input: TCreate) =>
      apiOrThrow<TRow>(basePath, { method: 'POST', json: input }),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: TUpdate }) =>
      apiOrThrow<TRow>(`${basePath}/${id}`, { method: 'PATCH', json: input }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      apiOrThrow<{ id: string; active: false }>(`${basePath}/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  return { list, create, update, remove };
}
